import { supabase } from '../supabaseClient.ts';
import type { WorkingAnswerFeedback } from './workingStepFeedback.ts';
import { parseAiPartFeedback } from './workingStepFeedback.ts';

type ShadowMarkTestPayload = {
  resultId: string;
};

type ShadowMarkPracticePayload = {
  practiceAnswerIds: string[];
};

type ShadowMarkAiPayload = {
  aiSubmissions: Array<{
    questionId: string;
    studentAnswer: string;
    workingSteps?: string[];
    practiceAnswerId?: string;
    questionResultId?: string;
  }>;
};

/**
 * Fire-and-forget shadow marking telemetry. Never blocks or throws to callers.
 */
export const invokeShadowMarkTestAttempt = (resultId: string): void => {
  if (!resultId) return;
  void supabase.functions
    .invoke('shadow_mark_submission', {
      body: { resultId } satisfies ShadowMarkTestPayload,
    })
    .then(({ error }) => {
      if (error) console.warn('shadow_mark_submission (test) failed:', error.message);
    })
    .catch((err) => {
      console.warn('shadow_mark_submission (test) error:', err);
    });
};

export const invokeShadowMarkPracticeAnswers = (practiceAnswerIds: string[]): void => {
  const ids = practiceAnswerIds.filter(Boolean);
  if (!ids.length) return;
  void supabase.functions
    .invoke('shadow_mark_submission', {
      body: { practiceAnswerIds: ids } satisfies ShadowMarkPracticePayload,
    })
    .then(({ error }) => {
      if (error) console.warn('shadow_mark_submission (practice) failed:', error.message);
    })
    .catch((err) => {
      console.warn('shadow_mark_submission (practice) error:', err);
    });
};

type AiSubmissionInput = {
  questionId: string;
  studentAnswer: string;
  workingSteps?: string[];
  practiceAnswerId?: string;
  questionResultId?: string;
};

/** AI marking for input questions without a canonical answer (worked-solution rubric). */
export type AiMarkInvokeResult = {
  awarded: number;
  maxMarks: number;
  feedback?: string;
  partFeedback?: WorkingAnswerFeedback;
  model?: string;
  tier?: string;
  confidence?: number | null;
};

export const invokeShadowMarkAiSubmission = async (
  submission: AiSubmissionInput,
  options?: { awaitResult?: boolean },
): Promise<AiMarkInvokeResult | null> => {
  if (!submission.questionId || !submission.studentAnswer?.trim()) return null;

  const invokePromise = supabase.functions.invoke('shadow_mark_submission', {
    body: {
      aiSubmissions: [
        {
          questionId: submission.questionId,
          studentAnswer: submission.studentAnswer,
          workingSteps: submission.workingSteps?.filter((s) => s.trim()) ?? [],
          practiceAnswerId: submission.practiceAnswerId,
          questionResultId: submission.questionResultId,
        },
      ],
    } satisfies ShadowMarkAiPayload,
  });

  if (!options?.awaitResult) {
    void invokePromise
      .then(({ error }) => {
        if (error) console.warn('shadow_mark_submission (ai) failed:', error.message);
      })
      .catch((err) => {
        console.warn('shadow_mark_submission (ai) error:', err);
      });
    return null;
  }

  try {
    const { data, error } = await invokePromise;
    if (error) {
      console.warn('shadow_mark_submission (ai) failed:', error.message);
      return null;
    }
    const outcomes = Array.isArray((data as { outcomes?: unknown[] } | null)?.outcomes)
      ? ((data as { outcomes: unknown[] }).outcomes as Array<Record<string, unknown>>)
      : [];
    const row = outcomes.find((o) => String(o.question_id ?? '') === submission.questionId);
    if (!row || row.error) return null;
    const awarded = Number(row.ai_awarded ?? 0);
    const maxMarks = Number(row.ai_max ?? 0);
    if (!Number.isFinite(awarded)) return null;
    return {
      awarded,
      maxMarks: Number.isFinite(maxMarks) ? maxMarks : 0,
      feedback: typeof row.ai_feedback === 'string' ? row.ai_feedback : undefined,
      partFeedback: parseAiPartFeedback(row.ai_part_feedback) ?? undefined,
      model: typeof row.ai_model === 'string' ? row.ai_model : undefined,
      tier: typeof row.ai_tier === 'string' ? row.ai_tier : undefined,
      confidence: Number.isFinite(Number(row.ai_confidence))
        ? Number(row.ai_confidence)
        : null,
    };
  } catch (err) {
    console.warn('shadow_mark_submission (ai) error:', err);
    return null;
  }
};

export type ContestMarkResult = {
  status: 'recheck' | 'escalated' | 'human_review';
  awarded?: number;
  maxMarks?: number;
  feedback?: string;
  model?: string;
  tier?: string;
  message?: string;
  contestId?: string;
  noEscalation?: boolean;
  error?: string;
};

export const invokeContestMark = async (input: {
  questionId: string;
  studentAnswer: string;
  workingSteps?: string[];
  practiceAnswerId?: string;
  questionResultId?: string;
  originalAwarded: number;
  originalMax: number;
  originalModel?: string;
  originalTier?: string;
  disputeReason?: string;
  recheckOnly?: boolean;
  requestHumanReview?: boolean;
  escalatedAwarded?: number;
  escalatedFeedback?: string;
  escalatedModel?: string;
  escalatedTier?: string;
}): Promise<ContestMarkResult> => {
  if (!input.questionId || !input.studentAnswer?.trim()) {
    throw new Error('A student answer is required to dispute this mark.');
  }

  try {
    const { data, error } = await supabase.functions.invoke('contest_mark', {
      body: {
        questionId: input.questionId,
        studentAnswer: input.studentAnswer,
        workingSteps: input.workingSteps?.filter((s) => s.trim()) ?? [],
        practiceAnswerId: input.practiceAnswerId,
        questionResultId: input.questionResultId,
        originalAwarded: input.originalAwarded,
        originalMax: input.originalMax,
        originalModel: input.originalModel,
        originalTier: input.originalTier ?? 'medium',
        student_dispute_reason: input.disputeReason?.trim() || undefined,
        disputeReason: input.disputeReason?.trim() || undefined,
        recheckOnly: input.recheckOnly === true,
        requestHumanReview: input.requestHumanReview === true,
        escalatedAwarded: input.escalatedAwarded,
        escalatedFeedback: input.escalatedFeedback,
        escalatedModel: input.escalatedModel,
        escalatedTier: input.escalatedTier,
      },
    });
    if (error) {
      console.warn('contest_mark failed:', error.message);
      throw new Error(error.message || 'contest_mark request failed');
    }
    const row = data as Record<string, unknown> | null;
    if (!row?.ok) {
      const msg = typeof row?.error === 'string' ? row.error : 'contest_mark returned an error';
      throw new Error(msg);
    }
    return {
      status: String(row.status ?? 'escalated') as ContestMarkResult['status'],
      awarded: Number(row.awarded ?? NaN),
      maxMarks: Number(row.max_marks ?? NaN),
      feedback: typeof row.feedback === 'string' ? row.feedback : undefined,
      model: typeof row.model === 'string' ? row.model : undefined,
      tier: typeof row.tier === 'string' ? row.tier : undefined,
      message: typeof row.message === 'string' ? row.message : undefined,
      contestId: typeof row.contest_id === 'string' ? row.contest_id : undefined,
      noEscalation: row.no_escalation === true,
    };
  } catch (err) {
    console.warn('contest_mark error:', err);
    if (err instanceof Error) throw err;
    throw new Error('contest_mark request failed');
  }
};
